import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PermissionService } from 'app/core/permission/services/permission.service';
import { ROUTES_PERMISSIONS } from 'app/common/constants/permission';
import { PermissionDirective } from 'app/core/permission/directives/permission.directive';
import { HasPermissionPipe } from 'app/core/permission/pipes/has-permission.pipe';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import {
  UserSelectorModalComponent,
  UserSelectorModalData,
  UserSelectorModalResult,
  MasterUser
} from 'app/shared/components/user-selector-modal/user-selector-modal.component';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { BusinessPartner } from 'app/modules/components/modals/dto/routes-detail-by-businesss-partnert-vehicle-dto';
import {
  VehicleRoutesModalComponent,
  VehicleRoutesModalData
} from 'app/shared/components/vehicle-routes-modal/vehicle-routes-modal.component';
import {
  VehicleTripsModalComponent,
  VehicleTripsModalData
} from 'app/shared/components/vehicle-trips-modal/vehicle-trips-modal.component';
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  CountButtonCellRendererComponent
} from 'app/shared/components/smart-grid';
import { BusinessPartnerNavCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/business-partner-nav-cell-renderer/business-partner-nav-cell-renderer.component';

@Component({
  selector: 'app-users-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SmartGridComponent,
    PermissionDirective,
    HasPermissionPipe
  ],
  templateUrl: './users-tab.component.html',
  styleUrl: './users-tab.component.scss'
})
export class UsersTabComponent {
  private readonly dialog = inject(MatDialog);
  private readonly businessPartnerService = inject(BusinessPartnerService);
  private readonly permissionService = inject(PermissionService);
  private readonly handlerMessage = inject(HandlerMessageService);

  readonly PERMISSIONS = ROUTES_PERMISSIONS.ASSOCIATIONS;

  /** Usuarios seleccionados */
  protected readonly selectedUsers = signal<MasterUser[]>([]);
  /** Business Partners con info de trips/subroutes */
  protected readonly businessPartnersInfo = signal<BusinessPartner[]>([]);
  protected readonly isLoading = signal<boolean>(false);

  /** Definición de columnas para SmartGrid */
  readonly columnDefs: ColDef<BusinessPartner>[] = [
    {
      field: 'name',
      headerName: 'Socio de Negocio',
      width: 200,
      cellRenderer: BusinessPartnerNavCellRendererComponent
    },
    {
      field: 'types' as any,
      headerName: 'Tipo',
      width: 120,
      valueFormatter: (value) => value?.[0]?.name || '-'
    },
    {
      field: 'externalKeys.code' as any,
      headerName: 'Código',
      width: 120,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'subRoutes' as any,
      headerName: 'Rutas',
      width: 80,
      cellRenderer: CountButtonCellRendererComponent,
      cellRendererParams: {
        countField: 'subRoutes',
        activeClass: 'bg-green-100 text-green-700',
        tooltip: 'Ver rutas asignadas',
        onClick: (bp: BusinessPartner) => this.openRoutesModal(bp)
      }
    },
    {
      field: 'currentTrips' as any,
      headerName: 'Viajes',
      width: 80,
      cellRenderer: CountButtonCellRendererComponent,
      cellRendererParams: {
        countField: 'currentTrips',
        activeClass: 'bg-blue-100 text-blue-700',
        tooltip: 'Ver viajes activos',
        onClick: (bp: BusinessPartner) => this.openTripsModal(bp)
      }
    }
  ];

  /** Configuración del SmartGrid */
  readonly gridConfig: GridConfig<BusinessPartner> = {
    rowIdField: '_id',
    selection: { enabled: false },
    pagination: { enabled: false },
    sorting: { enabled: false },
    search: { enabled: false },
    columnFilters: { enabled: false },
    styling: {
      maxHeight: '100%',
      stripedRows: true,
      density: 'comfortable'
    },
    emptyState: {
      message: 'No hay socios de negocio para mostrar',
      showIcon: true
    }
  };

  openUserSelector(): void {
    if (!this.checkIfHasPermission(this.PERMISSIONS.USERS, 'No tienes permisos para seleccionar usuarios.')) {
      return;
    }
    const dialogRef = this.dialog.open(UserSelectorModalComponent, {
      width: '95%',
      maxWidth: '800px',
      data: {
        selectedUsers: this.selectedUsers()
      } as UserSelectorModalData
    });

    dialogRef.afterClosed().subscribe((result: UserSelectorModalResult | undefined) => {
      if (!result) return;

      if (result.users?.length) {
        this.selectedUsers.set(result.users);
        this.loadBusinessPartnersInfo(result.users);
      } else {
        this.selectedUsers.set([]);
        this.businessPartnersInfo.set([]);
      }
    });
  }

  private loadBusinessPartnersInfo(users: MasterUser[]): void {
    // Extraer business_partner_id de cada usuario (filtrar los que no tienen)
    const businessPartnerIds = users
      .map(u => u.business_partner_id)
      .filter((id): id is string => !!id);

    if (businessPartnerIds.length === 0) {
      this.businessPartnersInfo.set([]);
      return;
    }

    this.isLoading.set(true);

    this.businessPartnerService.getWithTripsInfo(
      {
        limitPerPage: 100,
        currentPage: 1,
        sort: '_id',
        sortDirection: 'DESC'
      },
      businessPartnerIds
    ).subscribe({
      next: (response) => {
        this.businessPartnersInfo.set(response.data ?? []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  openRoutesModal(bp: BusinessPartner): void {
    if (!bp.subRoutes?.length) return;

    this.dialog.open(VehicleRoutesModalComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        subRoutes: bp.subRoutes,
        vehicleName: bp.name || bp.externalKeys?.code || 'Sin nombre'
      } as VehicleRoutesModalData
    });
  }

  openTripsModal(bp: BusinessPartner): void {
    if (!bp.currentTrips?.length) return;

    this.dialog.open(VehicleTripsModalComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        trips: bp.currentTrips,
        vehicleName: bp.name || bp.externalKeys?.code || 'Sin nombre'
      } as VehicleTripsModalData
    });
  }

  private checkIfHasPermission(permission: string, errorMessage?: string): boolean {
    const has = this.permissionService.has(permission);
    if (!has && errorMessage) {
      this.handlerMessage.error({
        message: errorMessage,
      });
    }
    return has;
  }
}
